// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// Constants imports
import { URLConstants } from '../../../../_constants/url-constants';
import { CacheDuration } from '../../../../_constants/cache-duration';

// Service imports
import { MPCacheRegistrationService } from '../../../../_services/shared-services/mp-cache-registration-service';

// model imports
import { APPaymentParameterDto } from '../../_models/accounts-payable/ap-payment/ap-payment-parameter-dto';
import { APPaymentResultDto } from '../../_models/accounts-payable/ap-payment/ap-payment-result-dto';
import { APPaymentVoidParametersDto } from '../../_models/accounts-payable/ap-payment/ap-payment-void-parameters-dto';
import { StatusMessageResultDto } from '../../../../_models/status-message-result-dto';
import { APPaymentUpdatePostDateParametersDto } from '../../_models/accounts-payable/ap-payment/ap-payment-update-post-date-parameters-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { APServiceChargeEditorParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-parameter-dto';
import { APServiceChargeEditorResultDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-result-dto';
import { APServiceChargeRawDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-raw-dto';
import { APServiceChargeEditorSubcategoriesParameterDto } from '../../_models/accounts-payable/service-charge/ap-service-charge-editor-subcategories-parameter-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { APPaymentVoidEditorResultDto } from '../../_models/accounts-payable/ap-payment/ap-payment-void-editor-result-dto';

@Injectable({
    providedIn: 'root'
})
export class APPaymentService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getAPPaymentDetail(param: APPaymentParameterDto): Observable<APPaymentResultDto> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/detail${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<APPaymentResultDto>(url, options)
            .pipe(map((response: APPaymentResultDto) => plainToClass(APPaymentResultDto, response)));
    }

    getAPPaymentDownloadDocument(param: APPaymentParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/download/document${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    voidPayment(baseParam: APPaymentVoidParametersDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .put<StatusMessageResultDto>(`${URLConstants.ccCBAPPaymentApiUrl}/void`, classToPlain(baseParam))
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    updatePaymentPostDate(baseParam: APPaymentUpdatePostDateParametersDto): Observable<any> {
        return this.httpClient
            .put<any>(`${URLConstants.ccCBAPPaymentApiUrl}/postdate`, classToPlain(baseParam))
            .pipe(map((responseData: any) => responseData));
    }

    getAPPayemntTransactionLog(baseParam: TransactionLogParameterDto): Observable<TransactionLogResultDto[]> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/history${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<TransactionLogResultDto[]>(url, options)
            .pipe(map((response: TransactionLogResultDto[]) => plainToClass(TransactionLogResultDto, response)));
    }

    getServiceChargeEditor(param: APServiceChargeEditorParameterDto): Observable<APServiceChargeEditorResultDto> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/servicecharge/editor${param.getQueryString()}`;
        return this.httpClient
            .get<APServiceChargeEditorResultDto>(url)
            .pipe(
                map((response: APServiceChargeEditorResultDto) =>
                    plainToClass(APServiceChargeEditorResultDto, response)
                )
            );
    }

    addServiceCharge(param: APServiceChargeRawDto): Observable<any> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/servicecharge`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getServiceChargeEditorSubcategories(
        params: APServiceChargeEditorSubcategoriesParameterDto
    ): Observable<GLSubcategorySelectorResultDto[]> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/subcategories${params.getQueryString()}`;
        return this.httpClient
            .get<GLSubcategorySelectorResultDto[]>(url)
            .pipe(
                map((response: GLSubcategorySelectorResultDto[]) =>
                    plainToClass(GLSubcategorySelectorResultDto, response)
                )
            );
    }

    getMinVoidDate(apPaymentId: number): Observable<APPaymentVoidEditorResultDto> {
        const url = `${URLConstants.ccCBAPPaymentApiUrl}/void/editor?apPaymentId=${apPaymentId}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(APPaymentVoidEditorResultDto, response)));
    }
}
